// OP-3605 — Remessas em curso (ESL1). Lista; a ação é na tela da remessa.
// OP-3619 — no celular a tabela vira cartão e o status vira chip.
import { FC, useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useEstoqueRemessas } from '../../../../hooks/estoque'
import { rotuloMetodoEnvio, RemessaRow, RemessasFilters, StatusRemessa } from '../../../../types/estoque'
import {
  BarraChips, BD, Button, CaixaLista, Card, Chip, Field, FiltersRow, ListaCartoes, PageSubtitle,
  PageTitle, Select, SoDesktop, Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { Cartao } from '../Cartao'
import { statusDaUrl } from '../filtrosUrl'
import { prazoUi } from '../formato'
import { ROTULO_ACAO, STATUS_REMESSA_UI, acaoDoStatus } from '../Remessa/regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const STATUS: StatusRemessa[] = ['DESPACHADA', 'ENTREGUE', 'EM_RETORNO', 'EM_CONFERENCIA', 'ENCERRADA', 'CANCELADA']
const ABERTAS: StatusRemessa[] = ['DESPACHADA', 'ENTREGUE', 'EM_RETORNO', 'EM_CONFERENCIA']
const COLS = 7

export const EstoqueRemessas: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const statusUrl = statusDaUrl(searchParams, STATUS)
  const [draft, setDraft] = useState<RemessasFilters>(statusUrl ? { status: statusUrl, page: 1 } : { page: 1 })
  const [applied, setApplied] = useState<RemessasFilters>(statusUrl ? { status: statusUrl, page: 1 } : { page: 1 })
  const { rows, total, page, lastPage, isLoading, isError, isUnauthorized } = useEstoqueRemessas(applied)

  useEffect(() => {
    if (!statusUrl) return
    setDraft((d) => ({ ...d, status: statusUrl, page: 1 }))
    setApplied((a) => ({ ...a, status: statusUrl, page: 1 }))
  }, [statusUrl])

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const escolherStatus = (status: StatusRemessa | '') => {
    setDraft((d) => ({ ...d, status, page: 1 }))
    setApplied((a) => ({ ...a, status, page: 1 }))
  }

  const retornoDe = (r: RemessaRow) => r.prorrogadoPara ?? r.previsaoRetorno

  const linha = (r: RemessaRow) => {
    const ui = STATUS_REMESSA_UI[r.status as StatusRemessa] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    return (
      <tr key={r.id} onClick={() => navigate(`/interno/estoque/remessas/${r.id}`)} style={{ cursor: 'pointer' }}>
        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.codigo}</td>
        <td><StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></td>
        <td><b>{r.detentorNome}</b><div style={{ fontSize: 12, color: BD.grayText }}>de {r.localOrigemNome}</div></td>
        <td>{rotuloMetodoEnvio(r.metodoEnvio)}{r.transportadora ? ` · ${r.transportadora}` : ''}{r.codigoRastreio ? ` · rastreio ${r.codigoRastreio}` : ''}</td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(r.despachadoEm)}</td>
        <td style={{ fontSize: 13 }}>{fmtDate(r.entregueEm)}</td>
        <td>{fmtDate(retornoDe(r))}{r.prorrogadoPara && <span style={{ fontSize: 11, color: BD.grayText }}> (prorrogada)</span>}</td>
      </tr>
    )
  }

  const cartao = (r: RemessaRow) => {
    const status = r.status as StatusRemessa
    const ui = STATUS_REMESSA_UI[status] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    const emCurso = ABERTAS.indexOf(status) >= 0
    const retorno = prazoUi(retornoDe(r), undefined, 'Retorno')
    const alerta = emCurso && retorno.alerta
    const acao = acaoDoStatus(status)
    return (
      <Cartao
        key={r.id}
        to={`/interno/estoque/remessas/${r.id}`}
        codigo={r.codigo}
        badge={ui}
        titulo={r.detentorNome}
        esmaecido={!emCurso}
        destaque={alerta}
        meta={(
          <>
            de {r.localOrigemNome} · {rotuloMetodoEnvio(r.metodoEnvio, 'sem método')}{r.transportadora ? ` · ${r.transportadora}` : ''}{r.codigoRastreio ? ` · rastreio ${r.codigoRastreio}` : ''}
            <div>despachada {fmtDate(r.despachadoEm)}{r.entregueEm ? ` · entregue ${fmtDate(r.entregueEm)}` : ''}</div>
          </>
        )}
        rodape={(
          <span style={{ color: alerta ? BD.redDark : BD.grayText, fontWeight: alerta ? 600 : 400 }}>
            {emCurso
              ? `${retorno.texto}${r.prorrogadoPara ? ' (prorrogada)' : ''}`
              : `Retorno ${fmtDate(retornoDe(r))}`}
          </span>
        )}
        rodapeFim={emCurso && acao ? ROTULO_ACAO[acao] : undefined}
      />
    )
  }

  return (
    <>
      <PageTitle>Remessas</PageTitle>
      <PageSubtitle>Equipamento que saiu e ainda não voltou: entrega, coleta, recebimento e conferência acontecem na tela da remessa.</PageSubtitle>

      <BarraChips role="group" aria-label="Status da remessa">
        <Chip type="button" $ativo={!applied.status} onClick={() => escolherStatus('')}>Todas</Chip>
        {STATUS.map((s) => (
          <Chip key={s} type="button" $ativo={applied.status === s} onClick={() => escolherStatus(s)}>
            {STATUS_REMESSA_UI[s].label}
          </Chip>
        ))}
      </BarraChips>

      <SoDesktop>
        <FiltersRow>
          <Field>
            <label>Status</label>
            <Select value={draft.status ?? ''} onChange={(e) => setDraft({ ...draft, status: e.target.value as StatusRemessa | '' })}>
              <option value="">todos</option>
              {STATUS.map((s) => <option key={s} value={s}>{STATUS_REMESSA_UI[s].label}</option>)}
            </Select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}><Button $variant="secondary" onClick={() => setApplied({ ...draft, page: 1 })}>Aplicar</Button></div>
        </FiltersRow>
      </SoDesktop>

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{isLoading ? 'Carregando…' : `${total} remessa${total === 1 ? '' : 's'}`}</div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Código</th><th>Status</th><th>Detentor</th><th>Envio</th><th>Despachada</th><th>Entregue</th><th>Retorno previsto</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>Não foi possível carregar as remessas.</td></tr>}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhuma remessa com os filtros atuais.</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && <Card style={{ padding: 20, color: BD.red }}>Não foi possível carregar as remessas.</Card>}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>Nenhuma remessa com os filtros atuais.</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={(page ?? 1) <= 1} onClick={() => setApplied((a) => ({ ...a, page: (page ?? 1) - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {page ?? 1}</span>
          <Button $variant="secondary" disabled={!!lastPage} onClick={() => setApplied((a) => ({ ...a, page: (page ?? 1) + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>
    </>
  )
}

export default EstoqueRemessas
